import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, FileText, CheckCircle, Clock, XCircle, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const DefectsPage = () => {
  const [defects, setDefects] = useState([]);
  const [selectedDefect, setSelectedDefect] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isResolveOpen, setIsResolveOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    client: ''
  });
  const [clients, setClients] = useState([]);
  const [resolveData, setResolveData] = useState({
    status: 'Resolved',
    resolution_notes: ''
  });

  useEffect(() => {
    fetchDefects();
    fetchClients();
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchDefects();
  }, [filters]); // eslint-disable-line

  const fetchDefects = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.client && filters.client !== 'all') params.append('client_id', filters.client);
      
      const response = await axios.get(`${API}/defects?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDefects(response.data);
    } catch (error) {
      toast.error('Failed to fetch defects');
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients');
    }
  };

  const updateDefectStatus = async (defectId, status, notes = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/defects/${defectId}/status`, 
        { status, resolution_notes: notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Defect marked as ${status}`);
      setIsResolveOpen(false);
      setResolveData({ status: 'Resolved', resolution_notes: '' });
      fetchDefects();
    } catch (error) {
      toast.error('Failed to update defect status');
    }
  };

  const convertToQuote = async (defectId) => {
    if (!window.confirm('Convert this defect to a repair quote?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/defects/${defectId}/convert-to-quote`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Quote ${response.data.quote_number} created successfully`);
      fetchDefects();
    } catch (error) {
      toast.error('Failed to convert to quote');
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      'Open': { color: 'bg-red-500', icon: XCircle },
      'In Progress': { color: 'bg-yellow-500', icon: Clock },
      'Resolved': { color: 'bg-green-500', icon: CheckCircle },
      'Closed': { color: 'bg-gray-500', icon: CheckCircle }
    };
    const config = configs[status] || configs['Open'];
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status}
      </Badge>
    );
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      'Critical': 'bg-red-600',
      'High': 'bg-orange-500',
      'Medium': 'bg-yellow-500',
      'Low': 'bg-blue-500'
    };
    return <Badge className={colors[severity] || 'bg-gray-500'}>{severity}</Badge>;
  };

  const viewDetails = (defect) => {
    setSelectedDefect(defect);
    setIsDetailsOpen(true);
  };

  const openResolveDialog = (defect) => {
    setSelectedDefect(defect);
    setIsResolveOpen(true);
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white">Defects Management</h1>
            <p className="text-gray-300 mt-1">Track and resolve asset defects</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-red-500">
              {defects.filter(d => d.status === 'Open').length} Open
            </Badge>
            <Badge className="bg-yellow-500">
              {defects.filter(d => d.status === 'In Progress').length} In Progress
            </Badge>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-white/5 border-white/10 mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters({ ...filters, status: value })}
              >
                <SelectTrigger className="bg-gray-800 border-gray-700">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Resolved">Resolved</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.client}
                onValueChange={(value) => setFilters({ ...filters, client: value })}
              >
                <SelectTrigger className="bg-gray-800 border-gray-700">
                  <SelectValue placeholder="Filter by client" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Defects List */}
        <div className="space-y-4">
          {defects.map((defect) => (
            <Card key={defect.id} className="bg-white/5 border-white/10 hover:bg-white/10 transition-all">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${
                      defect.severity === 'Critical' ? 'bg-red-500/20' :
                      defect.severity === 'High' ? 'bg-orange-500/20' :
                      defect.severity === 'Medium' ? 'bg-yellow-500/20' :
                      'bg-blue-500/20'
                    }`}>
                      <AlertTriangle className={`w-6 h-6 ${
                        defect.severity === 'Critical' ? 'text-red-500' :
                        defect.severity === 'High' ? 'text-orange-500' :
                        defect.severity === 'Medium' ? 'text-yellow-500' :
                        'text-blue-500'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-white">{defect.asset_type}</CardTitle>
                        <span className="text-gray-500">•</span>
                        <span className="text-gray-400 text-sm">{defect.asset_tag}</span>
                      </div>
                      <p className="text-sm text-gray-400 mt-1">{defect.defect_description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getSeverityBadge(defect.severity)}
                    {getStatusBadge(defect.status)}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <span className="text-gray-400 text-sm">Reported:</span>
                    <p className="text-white">{defect.reported_date}</p>
                  </div>
                  {defect.resolved_date && (
                    <div>
                      <span className="text-gray-400 text-sm">Resolved:</span>
                      <p className="text-white">{defect.resolved_date}</p>
                    </div>
                  )}
                  {defect.photo_urls && defect.photo_urls.length > 0 && (
                    <div>
                      <span className="text-gray-400 text-sm">Photos:</span>
                      <p className="text-white flex items-center gap-1">
                        <ImageIcon className="w-4 h-4" />
                        {defect.photo_urls.length} attached
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => viewDetails(defect)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    View Details
                  </Button>
                  {defect.status === 'Open' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => updateDefectStatus(defect.id, 'In Progress')}
                        className="bg-yellow-600 hover:bg-yellow-700"
                      >
                        Start Work
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => convertToQuote(defect.id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Create Quote
                      </Button>
                    </>
                  )}
                  {defect.status === 'In Progress' && (
                    <Button
                      size="sm"
                      onClick={() => openResolveDialog(defect)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark Resolved
                    </Button>
                  )}
                  {defect.status === 'Resolved' && (
                    <Button
                      size="sm"
                      onClick={() => updateDefectStatus(defect.id, 'Closed')}
                      variant="outline"
                    >
                      Close Defect
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {defects.length === 0 && (
          <div className="text-center py-12">
            <AlertTriangle className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400">No defects found. All assets are in good condition!</p>
          </div>
        )}

        {/* Defect Details Dialog */}
        <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
          <DialogContent className="bg-gray-900 text-white border-gray-800 max-w-3xl">
            <DialogHeader>
              <DialogTitle>Defect Details</DialogTitle>
            </DialogHeader>
            {selectedDefect && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-400">Asset Type</Label>
                    <p className="text-white font-semibold">{selectedDefect.asset_type}</p>
                  </div>
                  <div>
                    <Label className="text-gray-400">Asset Tag</Label>
                    <p className="text-white font-semibold">{selectedDefect.asset_tag}</p>
                  </div>
                  <div>
                    <Label className="text-gray-400">Severity</Label>
                    <div className="mt-1">{getSeverityBadge(selectedDefect.severity)}</div>
                  </div>
                  <div>
                    <Label className="text-gray-400">Status</Label>
                    <div className="mt-1">{getStatusBadge(selectedDefect.status)}</div>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-400">Description</Label>
                  <p className="text-white mt-1">{selectedDefect.defect_description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-400">Reported Date</Label>
                    <p className="text-white">{selectedDefect.reported_date}</p>
                  </div>
                  {selectedDefect.resolved_date && (
                    <div>
                      <Label className="text-gray-400">Resolved Date</Label>
                      <p className="text-white">{selectedDefect.resolved_date}</p>
                    </div>
                  )}
                </div>
                {selectedDefect.resolution_notes && (
                  <div>
                    <Label className="text-gray-400">Resolution Notes</Label>
                    <p className="text-white mt-1">{selectedDefect.resolution_notes}</p>
                  </div>
                )}
                {selectedDefect.photo_urls && selectedDefect.photo_urls.length > 0 && (
                  <div>
                    <Label className="text-gray-400">Photos ({selectedDefect.photo_urls.length})</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {selectedDefect.photo_urls.map((url, idx) => (
                        <div key={idx} className="aspect-square bg-gray-800 rounded-lg flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-gray-600" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {selectedDefect.linked_quote_id && (
                  <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3">
                    <p className="text-green-400 text-sm">
                      <FileText className="w-4 h-4 inline mr-2" />
                      Quote has been created for this defect
                    </p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Resolve Defect Dialog */}
        <Dialog open={isResolveOpen} onOpenChange={setIsResolveOpen}>
          <DialogContent className="bg-gray-900 text-white border-gray-800">
            <DialogHeader>
              <DialogTitle>Resolve Defect</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Status</Label>
                <Select
                  value={resolveData.status}
                  onValueChange={(value) => setResolveData({ ...resolveData, status: value })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="Resolved">Resolved</SelectItem>
                    <SelectItem value="Closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Resolution Notes</Label>
                <Textarea
                  value={resolveData.resolution_notes}
                  onChange={(e) => setResolveData({ ...resolveData, resolution_notes: e.target.value })}
                  placeholder="Describe how the defect was resolved..."
                  className="bg-gray-800 border-gray-700"
                  rows={4}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setIsResolveOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => updateDefectStatus(selectedDefect.id, resolveData.status, resolveData.resolution_notes)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default DefectsPage;
